import React from "react";
//#region types
import { InvoiceFormTypes } from "models/formTypes";
import { ForcedDimensions } from "models/propTypes/dimensionPropTypes";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
import { useInvoiceService } from "serviceHooks";
import * as Yup from "yup";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";

function useQuotationFormValidations() {
  const invoiceService = useInvoiceService();
  const allowedTypes = invoiceService.getCompatibleDimensionTypes(true);

  return React.useCallback((forcedDimensionMap: ForcedDimensions[]) => {
    const transformProducts = (value: InvoiceFormTypes.Product[]) => {
      return value.map((v) => ({
        ...v,
        dimensions: value[0].dimensions,
      }));
    };

    forcedDimensionMap = forcedDimensionMap.filter((dm) => allowedTypes.includes(dm.type.type));

    const productSchema = {
      id: Yup.number(),
      guid: Yup.string(),
      article: Yup.object<AutocompleteListItem>({
        id: Yup.number(),
        value: Yup.string(),
      }),
      price: Yup.number().required(TranslationKeys.Validations.Required),
      quantity: Yup.number().required(TranslationKeys.Validations.Required),
      unit: Yup.number(),
      tax: Yup.number().required(TranslationKeys.Validations.Required),
      totalAmountExTax: Yup.number(),
      taxText: Yup.string().required(TranslationKeys.Validations.Required),
      text: Yup.string(),
      periodisation: Yup.object(),
      recurringPeriodisation: Yup.object(),
      dimensions: Yup.array().of(Yup.string()).checkForcedDimensions(forcedDimensionMap),
      isEUCustomer: Yup.boolean(),
      eUVatCategoryId: Yup.number(),
    };

    return Yup.object<InvoiceFormTypes.Create>({
      id: Yup.number(),
      ownerId: Yup.number(),
      loadingCustomer: Yup.number().nullable().oneOf([null], TranslationKeys.Validations.Required),
      customer: Yup.object()
        .shape({ id: Yup.number(), value: Yup.string() })
        .default(undefined)
        .required(TranslationKeys.Validations.Required),
      referencePerson: Yup.object({ id: Yup.number(), value: Yup.string() }),
      isEUCustomer: Yup.boolean(),
      products: Yup.array<InvoiceFormTypes.Product>()
        .transform(transformProducts)
        .of(Yup.object<InvoiceFormTypes.Product>(productSchema))
        .required(TranslationKeys.Validations.AtLeastOneItem),
      total: Yup.number(),
      tax: Yup.number(),
      otherSettings: Yup.object<InvoiceFormTypes.OtherSettings>({
        invoiceDate: Yup.date()
          .required(TranslationKeys.Validations.Required)
          .typeError(TranslationKeys.Validations.InvalidDate),
        deliveryDate: Yup.date(),
        dueDate: Yup.date()
          .min(
            Yup.ref(FormFieldConstants.Invoice.OtherSettings.InvoiceDate),
            TranslationKeys.Validations.InvoiceDueDateGreaterThanInvoiceDate
          )
          .required(TranslationKeys.Validations.Required)
          .typeError(TranslationKeys.Validations.InvalidDate),
        paymentTerms: Yup.number()
          .typeError(TranslationKeys.Validations.InvalidPaymentTerms)
          .min(0, TranslationKeys.Validations.InvalidPaymentTerms),
        languageKey: Yup.string().required(TranslationKeys.Validations.Required),
        invoiceLayout: Yup.number(),
        showCurrencyCode: Yup.boolean(),
        showCustomerDeliveryAddress: Yup.boolean(),
        showReferenceInAddress: Yup.boolean(),
        sellerName: Yup.string(),
        attachments: Yup.array().of(
          Yup.object<UploadedFile>({
            contentType: Yup.string(),
            fileName: Yup.string(),
            fileSize: Yup.number(),
            data: Yup.string(),
          })
        ),
        reminderCost: Yup.number(),
        penaltyInterestType: Yup.number(),
        penaltyInterestPercentage: Yup.number(),
        isAutomaticRemindersEnabled: Yup.boolean(),
        automaticReminderDelay: Yup.number(),
        automaticReminderAmountLimit: Yup.number(),
        automaticReminderPeriod: Yup.number(),
        automaticReminderCountLimit: Yup.number(),
        automaticReminderSendType: Yup.number(),
        automaticReminderSendMail: Yup.object(),
      }),
      currency: Yup.string().required(TranslationKeys.Validations.Required),
      isDimensionsAdvancedConfiguration: Yup.boolean(),
      isPeriodisationAdvancedConfiguration: Yup.boolean(),
      recurringSettings: Yup.object(),
      rutRot: Yup.object<InvoiceFormTypes.RutRot>({
        houseWorkType: Yup.number(),
        rutRotArticles: Yup.array<InvoiceFormTypes.RutRotArticleItem>().when(
          FormFieldConstants.Invoice.RutRot.HouseWorkType,
          ([houseWorkType]: [number], scheme: ArraySchema<InvoiceFormTypes.RutRotArticleItem>) =>
            houseWorkType
              ? scheme
                  .required(TranslationKeys.Validations.AtLeastOneItem)
                  .min(1, TranslationKeys.Validations.AtLeastOneItem)
                  .of(
                    Yup.object<InvoiceFormTypes.RutRotArticleItem>({
                      id: Yup.number().required(TranslationKeys.Validations.Required),
                      name: Yup.string(),
                    })
                  )
              : scheme
        ),
      }),
    });
  }, []);
}

export default useQuotationFormValidations;
